import { ComponentProps, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { css } from '@emotion/react';
import { Ringtone as TenantRingtone } from '@weave/schema-gen-ts/dist/shared/phonedata/v1/tenant.pb';
import { useQueryClient } from 'react-query';
import { getJTI } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Button, DropdownField, useAlert, useTooltip } from '@frontend/design-system';
import { logError } from '../../../../../../utils/loggers';
import {
  RingtoneOption,
  formatMakeName,
  getMakeButtonColor,
  buildConsolidatedOptions,
  sortConsolidatedOptions,
  hasMultipleMakes,
} from './ringtone-dropdown.helpers';

export type { RingtoneOption } from './ringtone-dropdown.helpers';
export { DEFAULT_RINGTONE_VALUE } from './ringtone-dropdown.helpers';

type RingtoneDropdownProps = {
  label?: string;
  helperText?: string;
  /** Pre-filtered list of ringtone options to display. Sorting and filtering is the parent's responsibility. */
  ringtones: RingtoneOption[];
  /**
   * When true, ringtones sharing the same `ringtoneType` are collapsed into a single dropdown
   * option. The combined option shows a "/"-separated display name and one play button per
   * distinct ringtone, each with a tooltip showing the device make.
   * RINGTONE_SILENT is always consolidated regardless of this prop.
   */
  consolidateRingtoneType?: boolean;
} & Omit<ComponentProps<typeof DropdownField>, 'children' | 'label'>;

/**
 * A single play/stop toggle button for a ringtone preview.
 * When `showMakeColor` is true (i.e. the option is consolidated across makes),
 * the button icon is tinted with the make's brand color and hovering reveals a
 * tooltip with the device make name so the user knows which manufacturer's audio
 * file will play.
 */
const RingtonePlayButton = ({
  ringtone,
  isPlaying,
  onToggle,
  showMakeColor,
}: {
  ringtone: RingtoneOption;
  isPlaying: boolean;
  onToggle: () => void;
  /** When true, applies a make-specific icon color and shows a make name tooltip on hover. */
  showMakeColor: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();
  const makeColor = showMakeColor ? getMakeButtonColor(ringtone.make) : undefined;

  return (
    <>
      <Button
        {...(showMakeColor ? triggerProps : {})}
        iconName={isPlaying ? 'stop-small' : 'play-small'}
        label={isPlaying ? t('Stop ringtone') : t('Play ringtone')}
        variant='tertiary'
        css={css`
          ${makeColor
            ? `
            /* Override the icon color. The Button component sets an explicit color on the inner SVG
               via getIconColor(), so we need && (doubled specificity) on the svg selector to win. */
            && svg {
              color: ${makeColor};
            }
          `
            : ''}
        `}
        size='small'
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
      />
      {showMakeColor && <Tooltip {...tooltipProps}>{formatMakeName(ringtone.make)}</Tooltip>}
    </>
  );
};

/**
 * Dropdown for selecting a desk phone ringtone. Each option displays a play/stop button
 * that previews the ringtone audio on demand. Audio blobs are fetched with Bearer auth
 * and cached via React Query so subsequent previews do not re-fetch from the network.
 *
 * When `consolidateRingtoneType` is true, ringtones with the same `ringtoneType` are
 * grouped into a single dropdown option with combined display names and multiple play buttons.
 */
export const RingtoneDropdown = ({
  label,
  helperText,
  ringtones,
  consolidateRingtoneType,
  ...dropdownFieldProps
}: RingtoneDropdownProps) => {
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const queryClient = useQueryClient();

  // The consolidated dropdown options. These are ringtones that are grouped together by ringtoneType. Will only
  // consolidate if consolidateRingtoneType is true or if the ringtoneType is RINGTONE_SILENT (which should always
  // be consolidated).
  const consolidatedOptions = useMemo(() => {
    const options = buildConsolidatedOptions(ringtones, consolidateRingtoneType);
    options.sort(sortConsolidatedOptions);
    return options;
  }, [ringtones, consolidateRingtoneType]);

  const [playingMediaItemId, setPlayingMediaItemId] = useState<string | null>(null);
  const ringtoneAudioRef = useRef<HTMLAudioElement | null>(null);

  // Stop and release any playing audio when the component unmounts.
  useEffect(() => {
    return () => {
      if (ringtoneAudioRef.current) {
        ringtoneAudioRef.current.pause();
        ringtoneAudioRef.current = null;
      }
    };
  }, []);

  /**
   * Fetches and plays the ringtone audio identified by `mediaItemId`.
   * If the same ringtone is already playing, this acts as a toggle and stops it.
   * Audio blobs are cached indefinitely in React Query (keyed by mediaItemId)
   * so subsequent plays do not re-fetch from the network.
   * Bearer auth is only added when the URL does not already carry a token param.
   */
  const handlePlayRingtone = async (downloadUrl: string, mediaItemId: string) => {
    if (ringtoneAudioRef.current) {
      ringtoneAudioRef.current.pause();
      ringtoneAudioRef.current = null;
    }

    if (playingMediaItemId === mediaItemId) {
      setPlayingMediaItemId(null);
      return;
    }

    try {
      const audioUrl = await queryClient.fetchQuery({
        queryKey: ['cached-audio', mediaItemId],
        queryFn: async () => {
          const jtiToken = getJTI();
          const url = new URL(downloadUrl);
          const headers: Record<string, string> = {};

          if (!url.searchParams.has('token')) {
            headers['Authorization'] = `Bearer ${jtiToken}`;
          }

          const response = await fetch(downloadUrl, { headers });

          if (!response.ok) {
            throw new Error('Failed to fetch ringtone audio');
          }

          const blob = await response.blob();
          return URL.createObjectURL(blob);
        },
        staleTime: Infinity,
      });

      const audio = new Audio(audioUrl);
      ringtoneAudioRef.current = audio;

      audio.onended = () => {
        setPlayingMediaItemId(null);
        ringtoneAudioRef.current = null;
      };

      await audio.play();
      setPlayingMediaItemId(mediaItemId);
    } catch (err) {
      alerts.error(t('Failed to fetch ringtone audio'));
      logError('RingtoneDropdown:handlePlayRingtone | Failed to fetch ringtone audio', {
        data: { mediaItemId, downloadUrl, error: err },
      });
      setPlayingMediaItemId(null);
    }
  };

  // If the dropdown includes consolidated options that have ringtones from multiple makes, show make colors and
  // tooltips on all play buttons to help users distinguish which audio belongs to which make.
  const showMakeColors = hasMultipleMakes(consolidatedOptions);

  return (
    <DropdownField
      label={label ?? t('Select Desk Phone Ringtone')}
      placeholder={label ?? t('Select Desk Phone Ringtone')}
      helperText={
        helperText ?? t('Select a distinct ringtone for desk phones to help staff identify the purpose of the call')
      }
      {...dropdownFieldProps}
    >
      {consolidatedOptions.map((option) => (
        <DropdownField.Option
          key={option.key}
          value={option.ringtoneType}
          displayValue={option.displayName}
          css={css`
            padding-right: var(--fabric-space-gap-xs);
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: center;
              justify-content: space-between;
              width: 100%;
            `}
          >
            <span>{option.displayName}</span>
            {option.ringtoneType !== TenantRingtone.RINGTONE_SILENT && (
              <div
                css={css`
                  display: flex;
                  align-items: center;
                `}
              >
                {option.items.map((item, idx) => (
                  <Fragment key={item.mediaItemId || idx}>
                    {idx > 0 && (
                      <span
                        css={css`
                          margin: 0 2px;
                        `}
                      >
                        /
                      </span>
                    )}
                    {item.downloadUrl && (
                      <RingtonePlayButton
                        ringtone={item}
                        isPlaying={playingMediaItemId === item.mediaItemId}
                        onToggle={() => {
                          if (item.downloadUrl && item.mediaItemId) {
                            handlePlayRingtone(item.downloadUrl, item.mediaItemId);
                          }
                        }}
                        showMakeColor={showMakeColors}
                      />
                    )}
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </DropdownField.Option>
      ))}
    </DropdownField>
  );
};
